import { avatarSrc } from '../../utils/employeeAvatar';
import { useContext, useEffect, useState } from "react";
import { Avatar, Box, Sheet, Stack, Typography, Chip, Grid, LinearProgress, Checkbox } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import useMediaQuery from "@mui/material/useMediaQuery";

import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";

import { LayoutContext } from "../../context";
import { APIGetQualityTeamOverview } from "../../api";

const pctHex = (p) => (p >= 80 ? "#16a34a" : p >= 50 ? "#f59e0b" : "#dc2626");
const readLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

// Круговой индикатор процента
function Donut({ value = 0, color = "#6d28d9", size = 54, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const dash = (pct / 100) * c;
  return (
    <Box sx={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eee7fb" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography sx={{ fontWeight: 800, fontSize: "0.78rem", color }}>{Math.round(pct)}%</Typography>
      </Box>
    </Box>
  );
}

function ManagerCard({ m }) {
  const pct = m.overall_percent || 0;
  const color = pctHex(pct);
  const initials = (m.name || "?").split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase();

  // Единая метрика с «Целями менеджеров»: показываем СОЗДАННЫЕ события / цель,
  // как в GoalsPage (там actual_events_count). Завершённые — отдельной подписью.
  const evCreated = (m.actual_events_count ?? m.events_count) || 0; // созданные за месяц
  const evDone = m.completed_events || 0;                            // завершённые
  const evTarget = m.target_events_count || 0;
  const evProg = evTarget > 0
    ? (evCreated / evTarget) * 100
    : 0;

  const rev = m.revenue || 0;
  const revTarget = m.target_revenue || 0;
  const revProg = revTarget > 0 ? (rev / revTarget) * 100 : 0;

  // Средний чек — по завершённым (реально оплаченным), иначе по созданным
  const avg = evDone > 0 ? rev / evDone : (evCreated > 0 ? rev / evCreated : 0);
  const inWork = Math.max(evCreated - evDone, 0);
  const tier = pct >= 80 ? "Отлично" : pct >= 50 ? "В норме" : "Отстаёт";

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: "18px", p: 2, height: "100%", position: "relative", overflow: "hidden",
        transition: "transform .15s ease, box-shadow .15s ease", height: "-webkit-fill-available",
        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 28px -18px rgba(87,42,195,.5)" },
      }}
    >
      {/* Акцентная полоса по перформансу */}
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: m.has_goal ? color : "#d7d3e6" }} />

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.25 }}>
        <Avatar src={avatarSrc(m)} alt={m.name} sx={{
          width: 46, height: 46, borderRadius: "50%", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, color: "white", fontSize: "1rem",
          background: `linear-gradient(135deg, ${color}, ${color}bb)`,
        }}>{initials}</Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="title-md" sx={{ fontWeight: 700 }} noWrap>{m.name}</Typography>
          {m.has_goal ? (
            <Chip size="sm" variant="soft" sx={{ fontWeight: 700, color, "--Chip-minHeight": "20px" }}>{tier}</Chip>
          ) : (
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>Цель не поставлена</Typography>
          )}
        </Box>
        {m.has_goal && <Donut value={pct} color={color} size={64} />}
      </Stack>

      {/* События: СОЗДАННЫЕ / цель (как в «Целях менеджеров»); завершённые — подписью */}
      <Box sx={{ mb: 0.75 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.2 }}>
          <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 600 }}>События</Typography>
          <Typography level="body-xs" sx={{ fontWeight: 700 }}>
            {evCreated}{evTarget ? ` / ${evTarget}` : ""}
          </Typography>
        </Stack>
        <LinearProgress determinate value={Math.min(evProg, 100)} thickness={5}
          sx={{ borderRadius: "sm", "--LinearProgress-progressColor": "#6d28d9" }} />
        <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.25 }}>
          завершено: {evDone}
        </Typography>
      </Box>

      {/* Выручка */}
      <Box sx={{ mb: 0.75 }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.2 }}>
          <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 600 }}>Выручка</Typography>
          <Typography level="body-xs" sx={{ fontWeight: 700 }}>
            {Math.round(rev).toLocaleString("ru-RU")} ₽{revTarget ? ` / ${Math.round(revTarget).toLocaleString("ru-RU")}` : ""}
          </Typography>
        </Stack>
        <LinearProgress determinate value={Math.min(revProg, 100)} thickness={5}
          sx={{ borderRadius: "sm", "--LinearProgress-progressColor": "#f59e0b" }} />
      </Box>

      {/* Мини-метрики */}
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: "wrap" }}>
        <Chip size="sm" variant="soft" color="neutral" startDecorator={<PaidRoundedIcon sx={{ fontSize: 13 }} />}>
          Ср. чек {Math.round(avg).toLocaleString("ru-RU")} ₽
        </Chip>
        {inWork > 0 && (
          <Chip size="sm" variant="soft" color="primary" startDecorator={<EventNoteRoundedIcon sx={{ fontSize: 13 }} />}>
            В работе {inWork}
          </Chip>
        )}
      </Stack>
    </Sheet>
  );
}

// Плитки показателей менеджеров — для мастера по качеству и руководителя/админа
export default function QualityTeamOverview() {
  const { tokenEmployee, account } = useContext(LayoutContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const isMobile = useMediaQuery("(max-width:768px)");
  const [open, setOpen] = useState(() => {
    const saved = readLS("qto_open");
    if (saved === "0") return false;
    if (saved === "1") return true;
    return !isMobile; // по умолчанию свёрнуто на мобиле
  });
  const toggle = () => setOpen((v) => { const nv = !v; writeLS("qto_open", nv ? "1" : "0"); return nv; });

  useEffect(() => {
    if (!tokenEmployee) return;
    // блок показателей менеджеров виден только админу.
    const canSee = account?.role === "administrator";
    if (!canSee) return;

    setLoading(true);
    APIGetQualityTeamOverview(tokenEmployee)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tokenEmployee, account?.id]);

  const managers = data?.managers || [];
  const [showAll, setShowAll] = useState(false);
  if (!data || managers.length === 0) return null;

  const withGoal = managers.filter((m) => m.has_goal).length;
  // Что показываем в сетке: по умолчанию — только с целью, чтобы не засорять.
  const visibleManagers = showAll ? managers : managers.filter((m) => m.has_goal);
  const avgPct = withGoal
    ? Math.round(managers.filter((m) => m.has_goal).reduce((s, m) => s + (m.overall_percent || 0), 0) / withGoal)
    : 0;

  return (
    <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Sheet sx={{ borderRadius: "xl", border: "1px solid", borderColor: "divider", overflow: "hidden", bgcolor: "background.surface", mb: 2 }}>
        {/* Кликабельная шапка */}
        <Stack
          direction="row" alignItems="center" spacing={1.25} onClick={toggle}
          sx={{
            px: 2.5, py: 1.75, cursor: "pointer", userSelect: "none",
            borderBottom: open ? "1px solid" : "none", borderColor: "divider",
            background: "linear-gradient(135deg,#f3efff,#ede7ff)",
          }}
        >
          <Box sx={{ width: 36, height: 36, borderRadius: "lg", bgcolor: "primary.softBg", color: "primary.600", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <GroupsRoundedIcon />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography level="title-md" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Показатели менеджеров</Typography>
            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
              Контроль качества · текущий месяц · {managers.length} чел.
            </Typography>
          </Box>
          {/* Сводка видна даже когда свёрнуто */}
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 0.5, display: { xs: "none", sm: "flex" } }}>
            <Checkbox
              size="sm"
              label="Показать всех"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              onClick={(e) => e.stopPropagation()}   // чтобы клик не сворачивал блок
              sx={{ mr: 1 }}
            />
            <Chip size="sm" variant="soft" color="primary">{withGoal}/{managers.length} с целью</Chip>
            {withGoal > 0 && <Chip size="sm" variant="soft" sx={{ fontWeight: 700, color: pctHex(avgPct) }}>ср. {avgPct}%</Chip>}
          </Stack>
          <ExpandMoreRoundedIcon sx={{ transition: "transform .2s ease", transform: open ? "rotate(180deg)" : "none", color: "text.tertiary" }} />
        </Stack>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="body"
              initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22 }} style={{ overflow: "hidden" }}
            >
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Grid container spacing={2}>
                  {visibleManagers.map((m) => (
                    <Grid key={m.manager_id} xs={12} sm={6} md={6} lg={6} xl={4}>
                      <ManagerCard m={m} />
                    </Grid>
                  ))}
                </Grid>
                {visibleManagers.length === 0 && (
                  <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 1 }}>
                    Ни у кого не поставлена цель. Включите «Показать всех», чтобы увидеть список.
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {loading && <LinearProgress color="primary" sx={{ "--LinearProgress-thickness": "2px" }} />}
      </Sheet>
    </motion.div>
  );
}